import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Trash2, AlertTriangle, Mail, Download, Shield, FileText, Key, Copy, Plus, Terminal, Lock } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

function MCPKeysSection() {
  const [keys, setKeys] = useState([]);
  const [newKey, setNewKey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('claude');

  const fetchKeys = useCallback(async () => {
    try { const res = await fetch(`${API}/api/mcp/keys`, { credentials: 'include' }); if (res.ok) { const d = await res.json(); setKeys(d.keys || []); } } catch (e) { console.error("MCP keys fetch error:", e); }
    finally { setLoading(false); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchKeys(); }, [fetchKeys]);

  const generateKey = async () => {
    try {
      const res = await fetch(`${API}/api/mcp/keys/generate`, { method: 'POST', credentials: 'include' });
      if (!res.ok) { const d = await res.json(); throw new Error(d.detail); }
      const d = await res.json();
      setNewKey(d.key);
      fetchKeys();
      toast.success('MCP key generated!');
    } catch (e) { toast.error(e.message); }
  };

  const revokeKey = async (keyId) => {
    if (!window.confirm('Revoke this key? Any tools using it will stop working.')) return;
    await fetch(`${API}/api/mcp/keys/${keyId}`, { method: 'DELETE', credentials: 'include' });
    fetchKeys();
    toast.success('Key revoked');
  };

  const copyText = (text) => { navigator.clipboard.writeText(text); toast.success('Copied!'); };

  const claudeConfig = `{
  "mcpServers": {
    "sendpi": {
      "command": "npx",
      "args": ["-y", "sendpi-mcp-server"],
      "env": {
        "SENDPI_API_KEY": "${newKey || 'sk-sendpi-YOUR-KEY-HERE'}"
      }
    }
  }
}`;

  return (
    <div className="bg-[#0E1830] border border-white/10 rounded-xl p-6" data-testid="mcp-keys-section">
      <h2 className="text-lg font-semibold text-white mb-1 flex items-center gap-2"><Key className="w-5 h-5 text-violet-400" /> MCP & API Keys</h2>
      <p className="text-white/40 text-sm mb-5">Connect SendPI to Claude, ChatGPT, Cursor, and any AI tool.</p>

      {/* New key alert */}
      {newKey && (
        <div className="mb-4 p-4 bg-violet-500/10 border border-violet-500/30 rounded-lg" data-testid="new-key-alert">
          <p className="text-violet-300 text-xs font-bold mb-2">Copy this key now — you will not see it again.</p>
          <div className="flex items-center gap-2 bg-black/30 rounded px-3 py-2">
            <code className="text-emerald-400 text-sm flex-1 font-mono break-all">{newKey}</code>
            <button onClick={() => copyText(newKey)} className="text-white/50 hover:text-white shrink-0"><Copy className="w-4 h-4" /></button>
          </div>
        </div>
      )}

      {/* Key list */}
      {loading ? <p className="text-white/30 text-sm">Loading...</p> : keys.length === 0 ? (
        <div className="text-center py-6">
          <Key className="w-8 h-8 text-white/10 mx-auto mb-2" />
          <p className="text-white/30 text-sm mb-3">No MCP keys yet</p>
          <button onClick={generateKey} className="px-4 py-2 bg-violet-500/20 border border-violet-500/30 rounded-lg text-violet-300 text-sm hover:bg-violet-500/30 flex items-center gap-2 mx-auto" data-testid="generate-first-key-btn">
            <Plus className="w-4 h-4" /> Generate your first MCP key
          </button>
        </div>
      ) : (
        <>
          <div className="space-y-2 mb-4">
            {keys.map(k => (
              <div key={k.id} className="flex items-center gap-3 bg-white/[0.03] rounded-lg px-4 py-2.5" data-testid={`mcp-key-${k.id}`}>
                <code className="text-white/50 text-xs font-mono">{k.key_prefix}</code>
                <span className="text-white/20 text-[10px]">{k.created_at?.slice(0, 10)}</span>
                <span className="text-white/20 text-[10px]">Used: {k.last_used?.slice(0, 10) || 'never'}</span>
                <span className="ml-auto text-white/30 text-[10px]">{k.calls_today}/{k.tier_limit} today</span>
                <button onClick={() => revokeKey(k.id)} className="text-red-400/50 hover:text-red-400 text-xs">Revoke</button>
              </div>
            ))}
          </div>
          <button onClick={generateKey} className="px-3 py-1.5 bg-violet-500/10 border border-violet-500/20 rounded-lg text-violet-300 text-xs hover:bg-violet-500/20 flex items-center gap-1.5" data-testid="generate-key-btn">
            <Plus className="w-3 h-3" /> Add new key
          </button>
        </>
      )}

      {/* Install instructions */}
      <div className="mt-6 border-t border-white/[0.06] pt-5">
        <div className="flex gap-2 mb-3">
          {['claude', 'cursor'].map(t => (
            <button key={t} onClick={() => setTab(t)} className={`px-3 py-1.5 rounded-lg text-xs font-medium ${tab === t ? 'bg-violet-500/20 text-violet-300' : 'bg-white/5 text-white/40'}`}>
              {t === 'claude' ? 'Claude Desktop' : 'Cursor / Claude Code'}
            </button>
          ))}
        </div>
        {tab === 'claude' ? (
          <div className="space-y-3 text-sm">
            <p className="text-white/40">1. Generate an MCP key above</p>
            <p className="text-white/40">2. Add to your Claude Desktop config:</p>
            <div className="relative">
              <pre className="bg-black/30 rounded-lg p-3 text-[11px] text-emerald-400/80 font-mono overflow-x-auto">{claudeConfig}</pre>
              <button onClick={() => copyText(claudeConfig)} className="absolute top-2 right-2 text-white/30 hover:text-white"><Copy className="w-3.5 h-3.5" /></button>
            </div>
            <p className="text-white/30 text-xs">File: ~/Library/Application Support/Claude/claude_desktop_config.json</p>
            <p className="text-white/40">3. Restart Claude Desktop</p>
            <p className="text-white/40">4. Look for SendPI tools in your Claude sidebar</p>
          </div>
        ) : (
          <div className="space-y-3 text-sm">
            <p className="text-white/40">1. Generate an MCP key above</p>
            <p className="text-white/40">2. In Cursor Settings → MCP → Add server:</p>
            <div className="bg-black/30 rounded-lg p-3 text-[11px] text-white/60 font-mono space-y-1">
              <p>Name: <span className="text-emerald-400">SendPI</span></p>
              <p>Command: <span className="text-emerald-400">npx -y sendpi-mcp-server</span></p>
              <p>Environment: <span className="text-emerald-400">SENDPI_API_KEY={newKey || 'sk-sendpi-YOUR-KEY-HERE'}</span></p>
            </div>
            <p className="text-white/40">3. Reload Cursor</p>
            <p className="text-white/40">4. SendPI tools available in Agent mode</p>
          </div>
        )}
      </div>

      {/* Tier limits */}
      <div className="mt-4 flex gap-3 text-[10px]">
        <span className="text-white/25">Free: 100/day</span>
        <span className="text-white/25">Pro: 2,000/day</span>
        <span className="text-white/25">Max: Unlimited</span>
      </div>
    </div>
  );
}

function ChangePasswordForm() {
  const [current, setCurrent] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPwd.length < 6) { toast.error('New password must be at least 6 characters'); return; }
    if (newPwd !== confirm) { toast.error('Passwords do not match'); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/auth/change-password`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ current_password: current, new_password: newPwd }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed');
      toast.success('Password changed successfully');
      setCurrent(''); setNewPwd(''); setConfirm('');
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3 max-w-sm">
      <input
        type="password" placeholder="Current password" value={current}
        onChange={e => setCurrent(e.target.value)} data-testid="current-password-input"
        className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-white text-sm focus:outline-none focus:border-violet-500/50 placeholder-white/25"
      />
      <input
        type="password" placeholder="New password (min 6 chars)" value={newPwd}
        onChange={e => setNewPwd(e.target.value)} data-testid="new-password-input"
        className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-white text-sm focus:outline-none focus:border-violet-500/50 placeholder-white/25"
      />
      <input
        type="password" placeholder="Confirm new password" value={confirm}
        onChange={e => setConfirm(e.target.value)} data-testid="confirm-password-input"
        className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-white text-sm focus:outline-none focus:border-violet-500/50 placeholder-white/25"
      />
      <button
        type="submit" disabled={saving || !current || !newPwd || !confirm}
        data-testid="change-password-btn"
        className="px-5 py-2.5 bg-violet-500/20 border border-violet-500/30 rounded-lg text-violet-300 text-sm font-medium hover:bg-violet-500/30 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
      >
        {saving ? 'Changing...' : 'Change Password'}
      </button>
    </form>
  );
}

export default function SettingsPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');

  const handleExportData = async () => {
    setExporting(true);
    try {
      const res = await fetch(`${API}/api/auth/export-data`, { credentials: 'include' });
      if (!res.ok) throw new Error('Export failed');
      const data = await res.json();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `sendpi_data_export_${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Your data has been exported');
    } catch (e) { toast.error('Failed to export data'); }
    finally { setExporting(false); }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirm !== 'DELETE') return;
    setDeleting(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/auth/account`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ confirmation: 'DELETE' })
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Failed to delete account');
      }
      logout();
      navigate('/', { state: { message: 'Your account has been permanently deleted.' } });
    } catch (err) {
      setError(err.message);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div data-testid="settings-page" className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Settings</h1>
        <p className="text-white/50 mt-1">Manage your account</p>
      </div>

      {/* Account Info */}
      <div className="bg-[#0E1830] border border-white/10 rounded-xl p-6">
        <h2 className="text-lg font-semibold text-white mb-4">Account Information</h2>
        <div className="space-y-3 text-sm">
          <div className="flex justify-between">
            <span className="text-white/50">Email</span>
            <span className="text-white">{user?.email}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-white/50">Name</span>
            <span className="text-white">{user?.name}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-white/50">Plan</span>
            <span className="text-white capitalize">{user?.plan || 'free'}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-white/50">Credits</span>
            <span className="text-white">{user?.credits_remaining}</span>
          </div>
        </div>
      </div>

      {/* Change Password */}
      <div className="bg-[#0E1830] border border-white/10 rounded-xl p-6" data-testid="change-password-section">
        <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Lock className="w-5 h-5 text-violet-400" /> Change Password
        </h2>
        <ChangePasswordForm />
      </div>

      {/* Data Privacy & Rights */}
      <div className="bg-[#0E1830] border border-white/10 rounded-xl p-6" data-testid="data-privacy-section">
        <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Shield className="w-5 h-5 text-cyan-400" /> Your Data Rights
        </h2>
        <p className="text-white/50 text-sm mb-4">
          Under GDPR and CCPA, you have the right to access, export, and delete all your personal data.
        </p>
        <div className="flex flex-wrap gap-3">
          <button
            data-testid="export-data-btn"
            onClick={handleExportData}
            disabled={exporting}
            className="flex items-center gap-2 px-4 py-2.5 bg-cyan-500/10 border border-cyan-500/30 rounded-lg text-cyan-400 hover:bg-cyan-500/20 transition-colors text-sm disabled:opacity-50"
          >
            <Download className="w-4 h-4" />
            {exporting ? 'Exporting...' : 'Export My Data (JSON)'}
          </button>
          <a
            href="/privacy"
            className="flex items-center gap-2 px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white/60 hover:text-white transition-colors text-sm"
          >
            <FileText className="w-4 h-4" /> Privacy Policy
          </a>
          <a
            href="/do-not-sell"
            className="flex items-center gap-2 px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white/60 hover:text-white transition-colors text-sm"
          >
            <Shield className="w-4 h-4" /> Do Not Sell My Info
          </a>
        </div>
      </div>

      {/* Contact */}
      <div className="bg-[#0E1830] border border-white/10 rounded-xl p-6">
        <h2 className="text-lg font-semibold text-white mb-4">Support</h2>
        <div className="flex items-center gap-3 text-white/60">
          <Mail className="w-5 h-5" />
          <span>For any questions or data requests, email <a href="mailto:support@sendpi.ai" className="text-cyan-400 hover:underline">support@sendpi.ai</a></span>
        </div>
      </div>

      {/* MCP & API Keys */}
      <MCPKeysSection />

      {/* Danger Zone */}
      <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-6">
        <h2 className="text-lg font-semibold text-red-400 mb-2">Danger Zone</h2>
        <p className="text-white/50 text-sm mb-4">
          Once you delete your account, there is no going back. All your data will be permanently removed.
        </p>
        <button
          data-testid="delete-account-btn"
          onClick={() => setShowDeleteModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 hover:bg-red-500/20 transition-colors text-sm"
        >
          <Trash2 className="w-4 h-4" />
          Delete My Account
        </button>
      </div>

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-[#0E1830] border border-red-500/30 rounded-2xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-500/20 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-400" />
              </div>
              <h3 className="text-xl font-bold text-white">Delete Account</h3>
            </div>
            <p className="text-white/60 text-sm mb-6">
              This will permanently delete all your data. This cannot be undone. All your usage history, feedback, support tickets, and consent records will be erased.
            </p>
            <div className="mb-4">
              <label className="block text-sm text-white/50 mb-1.5">Type <span className="text-red-400 font-mono">DELETE</span> to confirm</label>
              <input
                data-testid="delete-confirm-input"
                type="text"
                value={deleteConfirm}
                onChange={(e) => setDeleteConfirm(e.target.value)}
                className="w-full bg-white/5 border border-red-500/30 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-red-500/50"
                placeholder="DELETE"
              />
            </div>
            {error && <p className="text-red-400 text-sm mb-3">{error}</p>}
            <div className="flex gap-3">
              <button
                onClick={() => { setShowDeleteModal(false); setDeleteConfirm(''); setError(''); }}
                className="flex-1 py-2.5 bg-white/10 text-white rounded-xl hover:bg-white/20 transition-colors"
              >
                Cancel
              </button>
              <button
                data-testid="delete-confirm-btn"
                onClick={handleDeleteAccount}
                disabled={deleteConfirm !== 'DELETE' || deleting}
                className="flex-1 py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              >
                {deleting ? 'Deleting...' : 'Delete Forever'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
